import { dal } from "@frontend/shared/data/frontend-dal";
import type { FrontendDAL } from "@frontend/shared/data/frontend-dal";
import { useGatedAction } from "../gated-action";

export interface EventManagerBundle {
  readonly createEvent: ReturnType<FrontendDAL["events"]["useCreateEvent"]>;
  readonly stageEventBrandingUpload: ReturnType<
    FrontendDAL["events"]["useStageEventBrandingUpload"]
  >;
}

export interface EventManagerActions {
  readonly isLoading: boolean;
  readonly error: Error | null;
  readonly manager: EventManagerBundle | null;
}

/**
 * Event-manager actions — mirrors the backend `EventManagerActions`. Always
 * returns a non-null result object; the inner `manager` bundle is `null` while
 * the current-user query is pending or when the caller lacks `user:event-manager`
 * (obtaining a non-null `manager` IS the create-event authorization). The backend
 * grants `user:event-manager` to every user (admin additive), so that is the only
 * role the frontend checks. `createEvent` is inert until `.mutate()`.
 */
export function useEventManagerActions(): EventManagerActions {
  const { isLoading, error, bundle } = useGatedAction({
    useDeps: () => ({
      currentUser: dal().auth.useCurrentUser(),
      createEvent: dal().events.useCreateEvent(),
      stageEventBrandingUpload: dal().events.useStageEventBrandingUpload(),
    }),
    isLoading: (d) => d.currentUser.isLoading,
    error: (d) =>
      d.currentUser.error instanceof Error ? d.currentUser.error : null,
    authorize: (d) => {
      const user = d.currentUser.data;
      if (user === null || user === undefined) return null;
      return user.$roles.includes("user:event-manager") ? user : null;
    },
    bundle: (d) => ({
      createEvent: d.createEvent,
      stageEventBrandingUpload: d.stageEventBrandingUpload,
    }),
    extra: () => ({}),
  });

  return { isLoading, error, manager: bundle };
}
